import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { getOrphanProducts, createProduct } from "../../../server/Product";
import { getFilters } from "../../../server/Filter";
import PopUp from "../../containers/PopUp";
import { Link } from "react-router-dom";
import "./Products.scss";

const productSchema = yup
  .object({
    ProductName: yup
      .string()
      .required("נדרש שם מוצר")
      .max(35, "שם המוצר חייב להיות עד 35 תווים"),
    Barcode: yup.string().required("נדרש ברקוד"),
    BrandId: yup.string().required("נדרש מותג"),
    PackageQuantity: yup.string().required("נדרש כמות באריזה"),
    WeightQuantity: yup.string().required("נדרש משקל"),
    CategoryId: yup.string().required("נדרשת קטגוריה"),
    StorageModeId: yup.string().required("נדרש מצב אחסון"),
    DairyId: yup.string().required("נדרש סוג"),
    KashrutId: yup.string().required("נדרשת כשרות"),
    ProviderId: yup.string().required("נדרש ספק"),
    UnitMeasureId: yup.string().required("נדרש יחידת משקל"),
  })
  .required();

const emptyProduct = {
  Barcode: "",
  BrandId: "",
  PackageQuantity: "",
  WeightQuantity: "",
  CategoryId: "",
  KashrutId: "",
  DairyId: "",
  StorageModeId: "",
  ProductName: "",
  ProviderId: "",
  UnitMeasureId: "",
};

const FormField = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  error,
  options = [],
}) => {
  const isSelect = type === "select";

  return (
    <div className="form-group">
      <label className="label">
        {label} <span className="required">*</span>
      </label>

      {isSelect ? (
        <select
          name={name}
          value={value}
          onChange={onChange}
          className={`select-input ${error ? "input-error" : ""}`}
        >
          <option value="">בחר {label}</option>
          {options.map((option) => (
            <option key={option.Id} value={option.Id}>
              {option.Name}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          className={`input ${error ? "input-error" : ""}`}
        />
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

const OrphanProductsList = ({ products }) => {
  if (products.length === 0) {
    return <p className="empty-message">לא נמצאו מוצרים יתומים</p>;
  }

  console.log(products);

  return (
    <ul className="products-list">
      {products.map((prod) => (
        <li key={prod.ProductId || prod.Mkt} className="product-item">
          <Link to={"/edit-product/" + prod.Mkt} className="product-link">
            <span dir="rtl">{prod.ProductName}</span>
            {prod.WeightQuantity && prod.UnitMeasure && (
              <>
                <span className="separator"> | </span>
                <span>
                  {prod.WeightQuantity} {prod.UnitMeasure}
                </span>
              </>
            )}
            {prod.Brand && (
              <>
                <span className="separator"> | </span>
                {prod.Brand}
              </>
            )}
          </Link>
        </li>
      ))}
    </ul>
  );
};

const Products = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [newProduct, setNewProduct] = useState({ ...emptyProduct });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    products,
    filterList,
    categories,
    parameters: { p_provider, p_storage_mode, p_unit_measure },
  } = useSelector((state) => ({
    products: state.displayProduct.itemsShow,
    filterList: state.displayProduct.filterList,
    categories: state.category.categories.filter((cat) => cat.FatherId === 0),
    parameters: state.parameters,
  }));

  useEffect(() => {
    dispatch(getFilters());
    dispatch(getOrphanProducts());
  }, [dispatch]);

  const openModal = () => {
    setIsModalOpen(true);
    setFormErrors({});
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setNewProduct({ ...emptyProduct });
    setFormErrors({});
  };

  const validateForm = () => {
    try {
      productSchema.validateSync(newProduct, { abortEarly: false });
      return {};
    } catch (err) {
      const errors = {};
      err.inner.forEach((error) => {
        errors[error.path] = error.message;
      });
      return errors;
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setNewProduct({
      ...newProduct,
      [name]: newValue,
    });

    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const response = await createProduct(newProduct);
      console.log(response);
      if (response.data.productMkt) {
        closeModal();
        navigate(`/edit-product/${response.data.productMkt}/information`);
      } else {
        setFormErrors({ general: "שגיאה ביצירת מוצר" });
      }
    } catch (error) {
      console.error("Error creating product:", error);
      setFormErrors({ general: "שגיאה ביצירת מוצר" });
    }
  };

  const fieldSources = {
    brands: filterList[0]?.Items || [],
    kashrut: filterList[1]?.Items || [],
    dairy: filterList[2]?.Items || [],
    providers: p_provider || [],
    storageModes: p_storage_mode || [],
    categories: categories || [],
    UnitMeasureId: p_unit_measure || [],
  };

  return (
    <div className="container">
      <div className="header">
        <button className="create-button" onClick={openModal}>
          יצירת מוצר חדש
        </button>
      </div>

      <h2 className="title">מוצרים יתומים:</h2>
      <OrphanProductsList products={products} />

      {isModalOpen && (
        <PopUp width={700} close={closeModal}>
          <div className="modal-content">
            <h3 className="modal-title">יצירת מוצר חדש</h3>

            <form onSubmit={handleSubmit} className="product-form">
              {formErrors.general && (
                <div className="error-message general-error">
                  {formErrors.general}
                </div>
              )}

              <div className="grid-container">
                <FormField
                  label="שם המוצר"
                  name="ProductName"
                  value={newProduct.ProductName}
                  onChange={handleInputChange}
                  error={formErrors.ProductName}
                />

                <FormField
                  label="ספק"
                  name="ProviderId"
                  type="select"
                  value={newProduct.ProviderId}
                  onChange={handleInputChange}
                  error={formErrors.ProviderId}
                  options={fieldSources.providers}
                />
              </div>

              <div className="grid-container">
                <FormField
                  label="ברקוד"
                  name="Barcode"
                  type="number"
                  value={newProduct.Barcode}
                  onChange={handleInputChange}
                  error={formErrors.Barcode}
                />

                <FormField
                  label="מותג"
                  name="BrandId"
                  type="select"
                  value={newProduct.BrandId}
                  onChange={handleInputChange}
                  error={formErrors.BrandId}
                  options={fieldSources.brands}
                />
              </div>

              <div className="grid-container">
                <FormField
                  label="משקל"
                  name="WeightQuantity"
                  type="number"
                  value={newProduct.WeightQuantity}
                  onChange={handleInputChange}
                  error={formErrors.WeightQuantity}
                />

                <FormField
                  label="יחידת משקל"
                  name="UnitMeasureId"
                  type="select"
                  value={newProduct.UnitMeasureId}
                  onChange={handleInputChange}
                  error={formErrors.UnitMeasureId}
                  options={fieldSources.UnitMeasureId}
                />
              </div>

              <div className="grid-container">
                <FormField
                  label="כמות באריזה"
                  name="PackageQuantity"
                  type="number"
                  value={newProduct.PackageQuantity}
                  onChange={handleInputChange}
                  error={formErrors.PackageQuantity}
                />

                <FormField
                  label="קטגוריה"
                  name="CategoryId"
                  type="select"
                  value={newProduct.CategoryId}
                  onChange={handleInputChange}
                  error={formErrors.CategoryId}
                  options={fieldSources.categories}
                />
              </div>

              <div className="grid-container">
                <FormField
                  label="מצב אחסון"
                  name="StorageModeId"
                  type="select"
                  value={newProduct.StorageModeId}
                  onChange={handleInputChange}
                  error={formErrors.StorageModeId}
                  options={fieldSources.storageModes}
                />

                <FormField
                  label="סוג"
                  name="DairyId"
                  type="select"
                  value={newProduct.DairyId}
                  onChange={handleInputChange}
                  error={formErrors.DairyId}
                  options={fieldSources.dairy}
                />

                <FormField
                  label="כשרות"
                  name="KashrutId"
                  type="select"
                  value={newProduct.KashrutId}
                  onChange={handleInputChange}
                  error={formErrors.KashrutId}
                  options={fieldSources.kashrut}
                />
                <div className="buttons-container">
                  <button type="submit" className="save-button">
                    שמור מוצר
                  </button>
                </div>
              </div>
            </form>
          </div>
        </PopUp>
      )}
    </div>
  );
};

export default Products;
